/**
 * B2B Outreach API client.
 *
 * All calls hit the admin-service (via api-gateway) and require admin auth.
 * Use the `apiCall` from `useAuth()` to invoke these helpers.
 */
import type { ApiCall } from './interview';

export const API_BASE =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const B2B = `${API_BASE}/api/v1/admin/b2b`;

// ============================================================
// Shared helper
// ============================================================

async function jsonOrThrow<T>(resp: Response, label: string): Promise<T> {
  if (!resp.ok) {
    let detail = `${resp.status} ${resp.statusText}`;
    try {
      const body = await resp.json();
      detail = (body as any).detail || detail;
    } catch {
      // ignore
    }
    throw new Error(`B2B ${label} failed: ${detail}`);
  }
  // 204 returns no body
  if (resp.status === 204) return undefined as unknown as T;
  return (await resp.json()) as T;
}

function qs(params: Record<string, any>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
}

// ============================================================
// Types
// ============================================================

export interface Company {
  id: number;
  domain: string;
  name: string | null;
  careers_url: string | null;
  ats_type: string | null;
  ats_slug: string | null;
  hq_country: string | null;
  hq_city: string | null;
  headcount_estimate: number | null;
  funding_stage: string | null;
  industry: string | null;
  tier: string;
  source: string | null;
  notes: string | null;
  last_outreach_at: string | null;
  outreach_count_90d: number;
  created_at: string;
  updated_at: string;
  open_jobs_count: number;
  contacts_count: number;
}

export interface Job {
  id: number;
  company_id: number;
  company_domain?: string;
  company_name?: string;
  external_id: string;
  ats_type: string;
  title: string | null;
  department: string | null;
  location: string | null;
  remote_status: string | null;
  seniority: string | null;
  category: string | null;
  tech_stack: string[] | null;
  description: string | null;
  description_summary: string | null;
  apply_url: string | null;
  posted_at: string | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  is_active: boolean;
  raw_data?: Record<string, unknown>;
}

export interface Contact {
  id: number;
  company_id: number;
  company_domain?: string;
  company_name?: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  linkedin_url: string | null;
  seniority: string | null;
  source: string | null;
  email_verified: boolean;
  email_verified_at: string | null;
  email_confidence: number | null;
  do_not_contact: boolean;
  do_not_contact_reason: string | null;
  last_contacted_at: string | null;
  total_sends: number;
  total_replies: number;
  created_at: string;
  updated_at: string;
}

export interface CampaignStats {
  drafts_total: number;
  drafts_pending_review: number;
  drafts_approved: number;
  drafts_sent: number;
  drafts_rejected: number;
  sends_today: number;
  replies_total: number;
  replies_positive: number;
}

export interface Campaign {
  id: number;
  name: string;
  status: string;
  mode: string;
  auto_send_threshold: number | null;
  from_mailbox: string | null;
  from_display_name: string | null;
  template_subject: string | null;
  template_body: string | null;
  rate_limit_per_day: number;
  followup_enabled: boolean;
  followup_delays_days: number[];
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  stats?: CampaignStats;
}

export interface Draft {
  id: number;
  campaign_id: number;
  campaign_name?: string;
  contact_id: number;
  company_id: number | null;
  contact_email: string;
  contact_first_name?: string;
  contact_last_name?: string;
  contact_title?: string;
  company_name?: string;
  company_domain?: string;
  subject: string | null;
  body_text: string | null;
  body_html: string | null;
  edited_subject: string | null;
  edited_body: string | null;
  personalization_tokens: Record<string, unknown> | null;
  generated_by_model: string | null;
  generated_at: string;
  status: string;
  confidence_score: number | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  reviewer_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface Send {
  id: number;
  draft_id: number | null;
  contact_id: number | null;
  campaign_id: number | null;
  campaign_name?: string;
  company_id?: number | null;
  company_name?: string;
  company_domain?: string;
  from_email: string;
  to_email: string;
  subject: string | null;
  body_sent: string | null;
  message_id: string;
  sent_at: string;
  bounced: boolean;
  bounce_reason: string | null;
  is_followup: boolean;
  parent_send_id: number | null;
  followup_step: number | null;
  has_reply: boolean;
  reply_intent: string | null;
}

export interface Reply {
  id: number;
  send_id: number | null;
  contact_id: number | null;
  contact_email?: string;
  company_name?: string;
  company_domain?: string;
  campaign_id?: number;
  campaign_name?: string;
  received_at: string;
  from_email: string | null;
  subject: string | null;
  body: string | null;
  in_reply_to_message_id: string | null;
  raw_headers: string | null;
  intent: string | null;
  intent_confidence: number | null;
  classified_at: string | null;
  classifier_model: string | null;
  handled: boolean;
  handled_by: string | null;
  handled_at: string | null;
  handler_notes: string | null;
  created_at: string;
}

export interface BlocklistEntry {
  id: number;
  email: string | null;
  domain: string | null;
  reason: string | null;
  source: string | null;
  added_by: string | null;
  created_at: string;
}

export interface HeadlineKPIs {
  companies_total: number;
  companies_with_ats: number;
  companies_with_open_jobs: number;
  contacts_total: number;
  contacts_verified: number;
  open_jobs_total: number;
  open_jobs_target_tech: number;
  open_jobs_unclassified: number;
  sends_24h: number;
  sends_7d: number;
  sends_30d: number;
  replies_24h: number;
  replies_7d: number;
  replies_30d: number;
  positive_replies_30d: number;
  reply_rate_30d: number;
  positive_rate_30d: number;
  unsubscribe_rate_30d: number;
}

export interface FunnelStep { label: string; count: number; }
export interface TimeseriesPoint { day: string; sends: number; replies: number; positive: number; }
export interface RecentEvent { when: string; kind: string; label: string; company_domain?: string; contact_email?: string; }

// ============================================================
// Companies
// ============================================================

export async function listCompanies(
  apiCall: ApiCall,
  params: {
    tier?: string; ats_type?: string; country?: string;
    min_open_jobs?: number; has_contacts?: boolean;
    has_ats?: boolean;
    source?: string;
    search?: string; limit?: number; offset?: number;
  } = {},
): Promise<Company[]> {
  const r = await apiCall(`${B2B}/companies${qs(params)}`);
  return jsonOrThrow<Company[]>(r, 'listCompanies');
}

export async function getCompany(apiCall: ApiCall, id: number): Promise<Company> {
  const r = await apiCall(`${B2B}/companies/${id}`);
  return jsonOrThrow<Company>(r, 'getCompany');
}

export async function createCompany(apiCall: ApiCall, payload: Partial<Company> & { domain: string }): Promise<Company> {
  const r = await apiCall(`${B2B}/companies`, { method: 'POST', body: JSON.stringify(payload) });
  return jsonOrThrow<Company>(r, 'createCompany');
}

export async function bulkAddCompanies(
  apiCall: ApiCall, domains: string[], source = 'manual_bulk', tier = 'cold',
): Promise<{ added: number; skipped_duplicates: number; skipped_invalid: number; company_ids: number[] }> {
  const r = await apiCall(`${B2B}/companies/bulk`, {
    method: 'POST',
    body: JSON.stringify({ domains, source, tier }),
  });
  return jsonOrThrow(r, 'bulkAddCompanies');
}

export async function uploadCompaniesCSV(apiCall: ApiCall, file: File) {
  const formData = new FormData();
  formData.append('file', file);
  const r = await apiCall(`${B2B}/companies/upload-csv`, { method: 'POST', body: formData });
  return jsonOrThrow(r, 'uploadCompaniesCSV');
}

export async function updateCompany(apiCall: ApiCall, id: number, payload: Partial<Company>): Promise<Company> {
  const r = await apiCall(`${B2B}/companies/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
  return jsonOrThrow<Company>(r, 'updateCompany');
}

export async function detectAts(apiCall: ApiCall, id: number) {
  const r = await apiCall(`${B2B}/companies/${id}/detect`, { method: 'POST' });
  return jsonOrThrow(r, 'detectAts');
}

// ----------------------------------------------------------------
// Decision-maker discovery helpers (manual LinkedIn-search workflow)
// ----------------------------------------------------------------

export interface EmailPattern {
  company_id: number;
  domain: string;
  pattern: string | null;
  confidence: number;
  example: string | null;
  examples_used: number;
  guess: string | null;
}

export async function getEmailPattern(
  apiCall: ApiCall,
  companyId: number,
  firstName?: string,
  lastName?: string,
): Promise<EmailPattern> {
  const params: Record<string, string> = {};
  if (firstName) params.first_name = firstName;
  if (lastName) params.last_name = lastName;
  const r = await apiCall(`${B2B}/companies/${companyId}/email-pattern${qs(params)}`);
  return jsonOrThrow<EmailPattern>(r, 'getEmailPattern');
}

export interface DecisionMakerSearchQuery {
  role_key: string;
  label: string;
  search_url: string;
}

export interface DecisionMakerSearch {
  company_id: number;
  company_name: string;
  domain: string;
  queries: DecisionMakerSearchQuery[];
}

export async function getDecisionMakerSearch(
  apiCall: ApiCall,
  companyId: number,
): Promise<DecisionMakerSearch> {
  const r = await apiCall(`${B2B}/companies/${companyId}/decision-maker-search`);
  return jsonOrThrow<DecisionMakerSearch>(r, 'getDecisionMakerSearch');
}

export interface DecisionMakerCandidate {
  name: string;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  linkedin_url: string | null;
  source: string;
  snippet: string | null;
  confidence: number;
  suggested_email: string | null;
}

export interface DecisionMakerDiagnostics {
  ddg: { attempted: boolean; hits: number; error: string | null };
  team_page: {
    pages_found: number;
    urls: string[];
    hits: number;
    error: string | null;
  };
  impressum: { found: boolean; hits: number; error: string | null };
}

export interface DecisionMakerDiscovery {
  company_id: number;
  company_name: string;
  domain: string;
  candidates: DecisionMakerCandidate[];
  pattern: string | null;
  pattern_confidence: number;
  pattern_examples_used: number;
  timings_ms: { discovery_ms: number; total_ms: number; candidates_found: number } | null;
  diagnostics: DecisionMakerDiagnostics | null;
}

export async function discoverDecisionMakers(
  apiCall: ApiCall,
  companyId: number,
): Promise<DecisionMakerDiscovery> {
  const r = await apiCall(`${B2B}/companies/${companyId}/discover-decision-makers`, {
    method: 'POST',
  });
  return jsonOrThrow<DecisionMakerDiscovery>(r, 'discoverDecisionMakers');
}

// ============================================================
// Jobs
// ============================================================

export async function listJobs(
  apiCall: ApiCall,
  params: {
    is_active?: boolean; ats_type?: string; category?: string;
    seniority?: string; company_id?: number; posted_within_days?: number;
    remote_status?: string; search?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<Job[]> {
  const r = await apiCall(`${B2B}/jobs${qs(params)}`);
  return jsonOrThrow<Job[]>(r, 'listJobs');
}

export async function getJob(apiCall: ApiCall, id: number): Promise<Job> {
  const r = await apiCall(`${B2B}/jobs/${id}`);
  return jsonOrThrow<Job>(r, 'getJob');
}

export async function listJobsByCompany(apiCall: ApiCall, companyId: number, isActive = true): Promise<Job[]> {
  const r = await apiCall(`${B2B}/jobs/by-company/${companyId}${qs({ is_active: isActive })}`);
  return jsonOrThrow<Job[]>(r, 'listJobsByCompany');
}

// ============================================================
// Contacts
// ============================================================

export async function listContacts(
  apiCall: ApiCall,
  params: {
    company_id?: number; has_verified?: boolean; do_not_contact?: boolean;
    seniority?: string; min_replies?: number; search?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<Contact[]> {
  const r = await apiCall(`${B2B}/contacts${qs(params)}`);
  return jsonOrThrow<Contact[]>(r, 'listContacts');
}

export async function createContact(apiCall: ApiCall, payload: Partial<Contact> & { company_id: number; email: string }): Promise<Contact> {
  const r = await apiCall(`${B2B}/contacts`, { method: 'POST', body: JSON.stringify(payload) });
  return jsonOrThrow<Contact>(r, 'createContact');
}

export async function updateContact(apiCall: ApiCall, id: number, payload: Partial<Contact>): Promise<Contact> {
  const r = await apiCall(`${B2B}/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
  return jsonOrThrow<Contact>(r, 'updateContact');
}

export async function markDoNotContact(apiCall: ApiCall, id: number, reason = 'manual_admin_action') {
  const r = await apiCall(`${B2B}/contacts/${id}/dnc${qs({ reason })}`, { method: 'POST' });
  return jsonOrThrow<Contact>(r, 'markDoNotContact');
}

export async function deleteContact(apiCall: ApiCall, id: number) {
  const r = await apiCall(`${B2B}/contacts/${id}`, { method: 'DELETE' });
  return jsonOrThrow<void>(r, 'deleteContact');
}

// ============================================================
// Campaigns
// ============================================================

export async function listCampaigns(
  apiCall: ApiCall, params: { status?: string; limit?: number; offset?: number; include_stats?: boolean } = {},
): Promise<Campaign[]> {
  const r = await apiCall(`${B2B}/campaigns${qs(params)}`);
  return jsonOrThrow<Campaign[]>(r, 'listCampaigns');
}

export async function createCampaign(apiCall: ApiCall, payload: Partial<Campaign>): Promise<Campaign> {
  const r = await apiCall(`${B2B}/campaigns`, { method: 'POST', body: JSON.stringify(payload) });
  return jsonOrThrow<Campaign>(r, 'createCampaign');
}

export async function getCampaign(apiCall: ApiCall, id: number): Promise<Campaign> {
  const r = await apiCall(`${B2B}/campaigns/${id}`);
  return jsonOrThrow<Campaign>(r, 'getCampaign');
}

export async function updateCampaign(apiCall: ApiCall, id: number, payload: Partial<Campaign>): Promise<Campaign> {
  const r = await apiCall(`${B2B}/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
  return jsonOrThrow<Campaign>(r, 'updateCampaign');
}

export async function activateCampaign(apiCall: ApiCall, id: number) {
  const r = await apiCall(`${B2B}/campaigns/${id}/activate`, { method: 'POST' });
  return jsonOrThrow<Campaign>(r, 'activateCampaign');
}

export async function pauseCampaign(apiCall: ApiCall, id: number) {
  const r = await apiCall(`${B2B}/campaigns/${id}/pause`, { method: 'POST' });
  return jsonOrThrow<Campaign>(r, 'pauseCampaign');
}

export async function completeCampaign(apiCall: ApiCall, id: number) {
  const r = await apiCall(`${B2B}/campaigns/${id}/complete`, { method: 'POST' });
  return jsonOrThrow<Campaign>(r, 'completeCampaign');
}

export async function deleteCampaign(apiCall: ApiCall, id: number) {
  const r = await apiCall(`${B2B}/campaigns/${id}`, { method: 'DELETE' });
  return jsonOrThrow<void>(r, 'deleteCampaign');
}

// ============================================================
// Drafts
// ============================================================

export async function listDrafts(
  apiCall: ApiCall, params: {
    status?: string; campaign_id?: number; company_id?: number;
    min_confidence?: number; search?: string; limit?: number; offset?: number;
  } = {},
): Promise<Draft[]> {
  const r = await apiCall(`${B2B}/drafts${qs(params)}`);
  return jsonOrThrow<Draft[]>(r, 'listDrafts');
}

export async function getDraft(apiCall: ApiCall, id: number): Promise<Draft> {
  const r = await apiCall(`${B2B}/drafts/${id}`);
  return jsonOrThrow<Draft>(r, 'getDraft');
}

export async function updateDraft(apiCall: ApiCall, id: number, payload: { edited_subject?: string; edited_body?: string; reviewer_notes?: string }) {
  const r = await apiCall(`${B2B}/drafts/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
  return jsonOrThrow<Draft>(r, 'updateDraft');
}

export async function approveDraft(apiCall: ApiCall, id: number) {
  const r = await apiCall(`${B2B}/drafts/${id}/approve`, { method: 'POST' });
  return jsonOrThrow<Draft>(r, 'approveDraft');
}

export async function rejectDraft(apiCall: ApiCall, id: number, reason?: string) {
  const r = await apiCall(`${B2B}/drafts/${id}/reject${qs(reason ? { reason } : {})}`, { method: 'POST' });
  return jsonOrThrow<Draft>(r, 'rejectDraft');
}

export async function regenerateDraft(apiCall: ApiCall, id: number) {
  const r = await apiCall(`${B2B}/drafts/${id}/regenerate`, { method: 'POST' });
  return jsonOrThrow<Draft>(r, 'regenerateDraft');
}

// ============================================================
// Sends
// ============================================================

export async function listSends(
  apiCall: ApiCall, params: {
    campaign_id?: number; contact_id?: number; bounced?: boolean;
    is_followup?: boolean; has_reply?: boolean; search?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<Send[]> {
  const r = await apiCall(`${B2B}/sends${qs(params)}`);
  return jsonOrThrow<Send[]>(r, 'listSends');
}

export async function getSend(apiCall: ApiCall, id: number): Promise<Send> {
  const r = await apiCall(`${B2B}/sends/${id}`);
  return jsonOrThrow<Send>(r, 'getSend');
}

// ============================================================
// Replies
// ============================================================

export async function listReplies(
  apiCall: ApiCall, params: {
    intent?: string; handled?: boolean; campaign_id?: number; search?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<Reply[]> {
  const r = await apiCall(`${B2B}/replies${qs(params)}`);
  return jsonOrThrow<Reply[]>(r, 'listReplies');
}

export async function getReply(apiCall: ApiCall, id: number): Promise<Reply> {
  const r = await apiCall(`${B2B}/replies/${id}`);
  return jsonOrThrow<Reply>(r, 'getReply');
}

export async function markReplyHandled(apiCall: ApiCall, id: number, notes?: string) {
  const r = await apiCall(`${B2B}/replies/${id}/handle${qs(notes ? { notes } : {})}`, { method: 'POST' });
  return jsonOrThrow<Reply>(r, 'markReplyHandled');
}

export async function reclassifyReply(apiCall: ApiCall, id: number, intent: string) {
  const r = await apiCall(`${B2B}/replies/${id}/reclassify${qs({ intent })}`, { method: 'POST' });
  return jsonOrThrow<Reply>(r, 'reclassifyReply');
}

// ============================================================
// Blocklist
// ============================================================

export async function listBlocklist(
  apiCall: ApiCall, params: { search?: string; source?: string; limit?: number; offset?: number } = {},
): Promise<BlocklistEntry[]> {
  const r = await apiCall(`${B2B}/blocklist${qs(params)}`);
  return jsonOrThrow<BlocklistEntry[]>(r, 'listBlocklist');
}

export async function addToBlocklist(apiCall: ApiCall, payload: { email?: string; domain?: string; reason?: string; source?: string }) {
  const r = await apiCall(`${B2B}/blocklist`, { method: 'POST', body: JSON.stringify({ source: 'manual', ...payload }) });
  return jsonOrThrow<BlocklistEntry>(r, 'addToBlocklist');
}

export async function removeFromBlocklist(apiCall: ApiCall, id: number) {
  const r = await apiCall(`${B2B}/blocklist/${id}`, { method: 'DELETE' });
  return jsonOrThrow<void>(r, 'removeFromBlocklist');
}

// ============================================================
// Dashboard
// ============================================================

export async function fetchSummary(apiCall: ApiCall): Promise<HeadlineKPIs> {
  const r = await apiCall(`${B2B}/dashboard/summary`);
  return jsonOrThrow<HeadlineKPIs>(r, 'fetchSummary');
}

export async function fetchFunnel(apiCall: ApiCall): Promise<{ steps: FunnelStep[] }> {
  const r = await apiCall(`${B2B}/dashboard/funnel`);
  return jsonOrThrow(r, 'fetchFunnel');
}

export async function fetchTimeseries(apiCall: ApiCall, days = 30): Promise<TimeseriesPoint[]> {
  const r = await apiCall(`${B2B}/dashboard/timeseries${qs({ days })}`);
  return jsonOrThrow<TimeseriesPoint[]>(r, 'fetchTimeseries');
}

export async function fetchRecent(apiCall: ApiCall, limit = 20): Promise<RecentEvent[]> {
  const r = await apiCall(`${B2B}/dashboard/recent${qs({ limit })}`);
  return jsonOrThrow<RecentEvent[]>(r, 'fetchRecent');
}

// ============================================================
// Discovery (Bundesagentur + full chain)
// ============================================================

export interface DiscoveryRun {
  id: string;
  kind:
    | 'bundesagentur' | 'full_chain' | 'ats_slugs'
    | 'discover_jobs' | 'classify_jobs' | 'discover_contacts' | 'score_icp'
    | 'generate_drafts' | 'send_batch' | 'poll_replies';
  status: 'queued' | 'running' | 'done' | 'error';
  params: any;
  started_at: number;
  finished_at: number | null;
  duration_s: number;
  stats: any;
  error: string | null;
  log: string[];
}

export interface DiscoveryPreviewEmployer {
  employer: string;
  title_sample: string | null;
  location: string | null;
  country: string | null;
  external_url: string | null;
}

export interface DiscoveryPreview {
  ba_total_jobs: number;
  page_size: number;
  unique_employers_in_page: number;
  sample: DiscoveryPreviewEmployer[];
}

export interface BundesagenturParams {
  keywords?: string[];
  location?: string;
  max_pages_per_keyword?: number;
  since_days?: number;
  concurrency?: number;
}

export interface FullChainParams {
  bundesagentur?: BundesagenturParams;
  run_discover_jobs?: boolean;
  run_classify_jobs?: boolean;
  run_discover_contacts?: boolean;
  run_generate_drafts?: boolean;
  campaign_id?: number;
}

export async function previewDiscovery(
  apiCall: ApiCall,
  params: { keyword?: string; location?: string; since_days?: number } = {},
): Promise<DiscoveryPreview> {
  const r = await apiCall(`${B2B}/discovery/preview${qs(params)}`);
  return jsonOrThrow<DiscoveryPreview>(r, 'previewDiscovery');
}

export async function triggerBundesagentur(
  apiCall: ApiCall, payload: BundesagenturParams = {},
): Promise<{ run_id: string; status: string; started_at: number }> {
  const r = await apiCall(`${B2B}/discovery/bundesagentur`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(r, 'triggerBundesagentur');
}

export async function triggerFullChain(
  apiCall: ApiCall, payload: FullChainParams = {},
): Promise<{ run_id: string; status: string; started_at: number }> {
  const r = await apiCall(`${B2B}/discovery/full-chain`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(r, 'triggerFullChain');
}

export interface AtsSlugsParams {
  ats?: string;
  concurrency?: number;
}

export interface CuratedAtsSlugs {
  total: number;
  per_ats: Record<string, Array<{ slug: string; brand: string }>>;
}

export async function triggerAtsSlugs(
  apiCall: ApiCall, payload: AtsSlugsParams = {},
): Promise<{ run_id: string; status: string; started_at: number }> {
  const r = await apiCall(`${B2B}/discovery/ats-slugs`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(r, 'triggerAtsSlugs');
}

export async function getCuratedAtsSlugs(
  apiCall: ApiCall,
): Promise<CuratedAtsSlugs> {
  const r = await apiCall(`${B2B}/discovery/ats-slugs/curated`);
  return jsonOrThrow<CuratedAtsSlugs>(r, 'getCuratedAtsSlugs');
}

export async function listDiscoveryRuns(
  apiCall: ApiCall,
): Promise<DiscoveryRun[]> {
  const r = await apiCall(`${B2B}/discovery/runs`);
  return jsonOrThrow<DiscoveryRun[]>(r, 'listDiscoveryRuns');
}

export async function getDiscoveryRun(
  apiCall: ApiCall, runId: string,
): Promise<DiscoveryRun> {
  const r = await apiCall(`${B2B}/discovery/runs/${runId}`);
  return jsonOrThrow<DiscoveryRun>(r, 'getDiscoveryRun');
}

export interface JobBoardCleanupCandidate {
  id: number;
  domain: string;
  name: string | null;
  source: string | null;
}

export interface JobBoardCleanupResult {
  dry_run: boolean;
  total_candidates?: number;
  candidates?: JobBoardCleanupCandidate[];
  deleted_count?: number;
  deleted_ids?: number[];
}

export async function cleanupJobBoards(
  apiCall: ApiCall,
  dryRun: boolean,
): Promise<JobBoardCleanupResult> {
  const r = await apiCall(`${B2B}/discovery/cleanup-job-boards${qs({ dry_run: dryRun })}`, {
    method: 'POST',
  });
  return jsonOrThrow<JobBoardCleanupResult>(r, 'cleanupJobBoards');
}

// ============================================================
// Pipeline workers
// ============================================================

export interface WorkerLimitParams {
  limit?: number;
  company_id?: number;
}

export interface DiscoverJobsParams extends WorkerLimitParams {
  /** Skip companies that already have active jobs (no refresh). */
  only_without_active_jobs?: boolean;
  /** Only companies without detected ATS. */
  only_without_ats?: boolean;
  /** Comma-separated tiers (e.g. cold,warm). Default skips blocked/lost. */
  tiers?: string;
}

export interface ClassifyJobsParams extends WorkerLimitParams {
  recategorize?: boolean;
  score_icp_after?: boolean;
}

export interface DiscoverContactsParams extends WorkerLimitParams {
  /** Include companies without open jobs. */
  all?: boolean;
  /** Re-scrape companies that already have 3+ contacts. */
  force?: boolean;
}

export interface DiscoverDecisionMakersParams extends WorkerLimitParams {
  /** Include companies without open jobs. */
  all?: boolean;
  /** Re-search even when a leader contact already exists. */
  force?: boolean;
}

export interface CampaignWorkerParams {
  campaign_id: number;
  limit?: number;
  max_sends?: number;
}

export async function triggerDiscoverJobs(
  apiCall: ApiCall, payload: DiscoverJobsParams = {},
): Promise<{ run_id: string; status: string; started_at: number }> {
  const r = await apiCall(`${B2B}/workers/discover-jobs`, {
    method: 'POST', body: JSON.stringify(payload),
  });
  return jsonOrThrow(r, 'triggerDiscoverJobs');
}

export async function triggerClassifyJobs(
  apiCall: ApiCall, payload: ClassifyJobsParams = {},
): Promise<{ run_id: string; status: string; started_at: number }> {
  const r = await apiCall(`${B2B}/workers/classify-jobs`, {
    method: 'POST', body: JSON.stringify(payload),
  });
  return jsonOrThrow(r, 'triggerClassifyJobs');
}

export async function triggerDiscoverContacts(
  apiCall: ApiCall, payload: DiscoverContactsParams = {},
): Promise<{ run_id: string; status: string; started_at: number }> {
  const r = await apiCall(`${B2B}/workers/discover-contacts`, {
    method: 'POST', body: JSON.stringify(payload),
  });
  return jsonOrThrow(r, 'triggerDiscoverContacts');
}

export async function triggerDiscoverDecisionMakers(
  apiCall: ApiCall, payload: DiscoverDecisionMakersParams = {},
): Promise<{ run_id: string; status: string; started_at: number }> {
  const r = await apiCall(`${B2B}/workers/discover-decision-makers`, {
    method: 'POST', body: JSON.stringify(payload),
  });
  return jsonOrThrow(r, 'triggerDiscoverDecisionMakers');
}

export async function triggerScoreIcp(
  apiCall: ApiCall,
): Promise<{ run_id: string; status: string; started_at: number }> {
  const r = await apiCall(`${B2B}/workers/score-icp`, { method: 'POST', body: '{}' });
  return jsonOrThrow(r, 'triggerScoreIcp');
}

export async function triggerGenerateDrafts(
  apiCall: ApiCall, payload: CampaignWorkerParams,
): Promise<{ run_id: string; status: string; started_at: number }> {
  const r = await apiCall(`${B2B}/workers/generate-drafts`, {
    method: 'POST', body: JSON.stringify(payload),
  });
  return jsonOrThrow(r, 'triggerGenerateDrafts');
}

export async function triggerSendBatch(
  apiCall: ApiCall, payload: CampaignWorkerParams,
): Promise<{ run_id: string; status: string; started_at: number }> {
  const r = await apiCall(`${B2B}/workers/send-batch`, {
    method: 'POST', body: JSON.stringify(payload),
  });
  return jsonOrThrow(r, 'triggerSendBatch');
}

export async function triggerPollReplies(
  apiCall: ApiCall, mailbox?: string,
): Promise<{ run_id: string; status: string; started_at: number }> {
  const r = await apiCall(`${B2B}/workers/poll-replies`, {
    method: 'POST', body: JSON.stringify({ mailbox: mailbox || null }),
  });
  return jsonOrThrow(r, 'triggerPollReplies');
}

export interface DuplicateGroup {
  name_key: string;
  display_name: string;
  domains: string[];
  canonical_id: number;
  merge_ids: number[];
}

export interface DuplicateCandidatesResult {
  total_groups: number;
  total_merge_candidates: number;
  groups: DuplicateGroup[];
}

export async function previewDuplicateCandidates(
  apiCall: ApiCall,
): Promise<DuplicateCandidatesResult> {
  const r = await apiCall(`${B2B}/companies/duplicate-candidates`);
  return jsonOrThrow<DuplicateCandidatesResult>(r, 'previewDuplicateCandidates');
}

export async function mergeDuplicateCompanies(
  apiCall: ApiCall, dryRun: boolean,
): Promise<{ dry_run: boolean; groups_found?: number; companies_merged?: number; groups?: DuplicateGroup[] }> {
  const r = await apiCall(`${B2B}/companies/merge-duplicates${qs({ dry_run: dryRun })}`, {
    method: 'POST',
  });
  return jsonOrThrow(r, 'mergeDuplicateCompanies');
}
